import { Alert, Button, Page, Skeleton, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useEnv } from '../../../context/env-context';
import { withPageProviders } from '../../../context/page-context';
import { SyncLogStatusAlert } from '../../sync';
import { SyncLogErrorTable } from '../../sync/components/sync-log-error-table';
import { useSyncApi } from '../../sync/hooks/use-sync-api';
import { useSyncStatus } from '../../sync/hooks/use-sync-log-status';
import type { SyncLogIdentifier } from '../../sync/types';
import { CustomerSearchBar } from '../components/customers-search-table';
export const StaffCustomerSyncPage = withPageProviders(() => {
  const env = useEnv();
  const syncLogId: SyncLogIdentifier = { integration: env.platform ?? '', dataType: 'customers' };
  const { data: status, isLoading, error, refetch } = useSyncStatus(syncLogId);
  const [isManualLoading, setIsManualLoading] = useState<boolean>(false);
  const { syncApi } = useSyncApi();
  const [searchValue, setSearchValue] = useState('');

  if (error || !status) {
    return (
      <Alert
        variant="error"
        title={t('features:synclog.components.sync_log_status_alert.load_error.title')}
        headingLevel="h2"
      >
        <div className="flex w-full flex-col items-start gap-2">
          <Text>
            {t('features:synclog.components.sync_log_status_alert.load_error.description')}
          </Text>
          <Button onClick={() => refetch()} variant="secondary">
            {t('buttons:reload')}
          </Button>
        </div>
      </Alert>
    );
  }

  const isSyncLogErrors = status.error_count > 0;

  const handleTriggerSync = () => {
    setIsManualLoading(true);
    syncApi
      .triggerManualSync({
        teamSlug: env.team.slugged_name,
        environment: env.environment,
        dataType: syncLogId.dataType,
      })
      .then(() => refetch())
      .finally(() => setIsManualLoading(false));
  };

  return (
    <Page
      title={t('features:customers.pages.staff_customer_sync_page.title')}
      description={t('features:customers.pages.staff_customer_sync_page.description')}
      primaryAction={{
        label: t('features:customers.pages.staff_customer_sync_page.actions.manual_sync'),
        onClick: () => handleTriggerSync(),
      }}
      secondaryActions={[
        {
          label: t('buttons:reload'),
          onClick: () => refetch(),
        },
      ]}
    >
      {isLoading || isManualLoading ? (
        <Skeleton />
      ) : (
        <div className="w-full">
          <SyncLogStatusAlert syncFor={syncLogId} allowViewErrors={false} />
          {isSyncLogErrors && (
            <>
              <div className="mt-4">
                <CustomerSearchBar
                  onSearchChange={setSearchValue}
                  placeholder={t(
                    'features:customers.pages.staff_customer_sync_page.search_placeholder',
                  )}
                />
              </div>
              <div className="mt-4">
                <SyncLogErrorTable
                  syncFor={syncLogId}
                  showApiErrors={true}
                  searchValue={searchValue}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Page>
  );
});
